import { ingestionDataTypeOptions, ingestionFileTypeOptions, isSourcedFromMasterFile, resolveMasterFileLocationValue, resolveSource, sourceOptions } from './IngestionDetails.Helpers';
import fileExtension from '../../../infrastructure/constants/fileExtension';
import FormGroup from '../../Common/FormGroup/FormGroup';
import guidance from './IngestionDetails.Guidance';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import RadioButtonGroup from '../../Common/RadioButtonGroup/RadioButtonGroup';
import React from 'react';
import SelectList from '../../Common/SelectList/SelectList';
import SeparatedValueBuilder from '../SeparatedValueBuilder/SeparatedValueBuilder';
import separatedValueHelper from '../../../infrastructure/helpers/ingestion/separatedValueHelper';
import validateEmptyValueTokens from './IngestionDetails.Validation.EmptyValueTokens';
import validateFilenames from '../IngestionFilenames/IngestionFilenames.Validation';
import Modal from '../../Common/Modal/Modal';
import appRoutes from '../../../infrastructure/constants/appRoutes';
import urlHelper from '../../../infrastructure/helpers/common/urlHelper';
import './IngestionDetails.scss';
import copyObject from '../../../infrastructure/helpers/common/copyObject';

interface Props {
    isReadOnly: boolean;
    config: IngestionConfig;
    onChange: (config: IngestionConfig) => void;
}

interface State {
    source: string;
    showReportConfigsPopup: boolean;
}

class IngestionDetails extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = { source: resolveSource(this.props.config), showReportConfigsPopup: false }

        this.onNameChange = this.onNameChange.bind(this);
        this.onFilenamesChange = this.onFilenamesChange.bind(this);
        this.onSourceChange = this.onSourceChange.bind(this);
        this.onMasterFileLocationChange = this.onMasterFileLocationChange.bind(this);
        this.onDescriptionChange = this.onDescriptionChange.bind(this);
        this.onDataSourceKindChange = this.onDataSourceKindChange.bind(this);
        this.onEmptyValueTokenChange = this.onEmptyValueTokenChange.bind(this);
        this.onTransposedDataChange = this.onTransposedDataChange.bind(this);
        this.onFileTypeChange = this.onFileTypeChange.bind(this);
    }

    onNameChange(e: React.ChangeEvent<HTMLInputElement>): void {

        let config = copyObject(this.props.config);

        config.name = e.target.value;

        this.props.onChange(config);
    }

    onFilenamesChange(value: string): void {

        let config = copyObject(this.props.config);

        config.filenames = value;

        this.props.onChange(config);
    }

    onSourceChange(source: string): void {

        let config = copyObject(this.props.config);

        let $default = separatedValueHelper.split(this.props.config.filenames)[0];

        if (source === fileExtension.xml) {

            config.xmlSectionName = $default;
            config.jsonSectionName = '';
        }
        else if (source === fileExtension.json) {

            config.jsonSectionName = $default;
            config.xmlSectionName = '';
        }
        else {
            config.xmlSectionName = '';
            config.jsonSectionName = '';
        }

        this.props.onChange(config);

        this.setState({ source: source });
    }

    onMasterFileLocationChange(e: React.ChangeEvent<HTMLInputElement>): void {

        let config = copyObject(this.props.config);

        if (this.state.source === fileExtension.xml) {

            config.xmlSectionName = e.target.value;
            this.props.onChange(config);
            return;
        }

        if (this.state.source === fileExtension.json) {

            config.jsonSectionName = e.target.value;
            this.props.onChange(config);
            return;
        }
    }

    onDescriptionChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {

        let config = copyObject(this.props.config);

        config.description=e.target.value;

        this.props.onChange(config);
    }

    onDataSourceKindChange(value: string): void {

        let config = copyObject(this.props.config);

        config.dataSourceKind= value ;

        this.props.onChange(config);
    }

    onEmptyValueTokenChange(value: string): void {

        let config = copyObject(this.props.config);

        config.emptyValueTokens= value ;

        this.props.onChange(config);
    }

    onTransposedDataChange(e: React.ChangeEvent<HTMLInputElement>): void {

        let config = copyObject(this.props.config);

        config.isTransposed= e.target.checked;

        this.props.onChange(config);
    }

    onFileTypeChange(value: string): void {

        let config = copyObject(this.props.config);

        config.fileType = value;

        this.props.onChange(config);
    }

    render(): JSX.Element {
        const labelClass = 'col-5';
        const contentClass = 'col-7';

        return (
            <>
                <FormGroup label='Report Configs' labelClassName={labelClass} contentClassName={contentClass} info={guidance.reportConfigUsingIngestion}>
                    <button className='btn btn-light line-height-1' onClick={() => this.setState({ showReportConfigsPopup: true })}>Show</button>

                </FormGroup>
                <FormGroup label="File extension:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.fileType}>
                    <SelectList
                        className="form-control"
                        options={ingestionFileTypeOptions}
                        value={this.props.config.fileType}
                        onChange={this.onFileTypeChange}
                        disabled={this.props.isReadOnly} />
                </FormGroup>

                <FormGroup label="Config Name:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.name}>
                    <input
                        type="text"
                        className="form-control"
                        value={this.props.config.name}
                        onChange={this.onNameChange}
                        disabled={this.props.isReadOnly} />
                </FormGroup>

                <FormGroup label="File Names:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.filenames}>
                    <SeparatedValueBuilder
                        isReadOnly={this.props.isReadOnly}
                        items={this.props.config.filenames}
                        suffix={`.${isSourcedFromMasterFile(this.props.config) ? this.state.source : this.props.config.fileType}`}
                        validate={validateFilenames}
                        onChange={this.onFilenamesChange} />
                </FormGroup>
                {
                    this.props.config.fileType === fileExtension.csv &&
                    <FormGroup label="Sourced From:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.sourcedFrom}>

                        <div className="mt-2">
                            <RadioButtonGroup
                                options={sourceOptions}
                                value={this.state.source}
                                onChange={this.onSourceChange}
                                isDisabled={() => this.props.isReadOnly}
                                name="sourced-from" />
                        </div>
                    </FormGroup>
                }
                {
                    this.state.source !== fileExtension.csv &&
                    <FormGroup label={`${this.state.source.toUpperCase()} Section Name:`} labelClassName={labelClass} contentClassName={contentClass} info={guidance.masterFileLocation}>
                        <input
                            type="text"
                            className="form-control"
                            value={resolveMasterFileLocationValue(this.state.source, this.props.config)}
                            onChange={this.onMasterFileLocationChange}
                            disabled={this.props.isReadOnly} />
                    </FormGroup>
                }

                <FormGroup label="Description:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.description}>
                    <textarea
                        className="form-control"
                        value={this.props.config.description}
                        onChange={this.onDescriptionChange}
                        disabled={this.props.isReadOnly}>
                    </textarea>
                </FormGroup>

                <FormGroup label="Data type:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.dataSourceKind}>
                    <SelectList
                        className="form-control"
                        options={ingestionDataTypeOptions}
                        value={this.props.config.dataSourceKind}
                        onChange={this.onDataSourceKindChange}
                        disabled={this.props.isReadOnly} />
                </FormGroup>

                <FormGroup label="Empty value tokens:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.emptyValueToken}>
                    <SeparatedValueBuilder
                        isReadOnly={this.props.isReadOnly}
                        items={this.props.config.emptyValueTokens || ''}
                        suffix={null}
                        validate={validateEmptyValueTokens}
                        onChange={this.onEmptyValueTokenChange} />
                </FormGroup>

                {
                    this.props.config.fileType === fileExtension.json &&
                    <FormGroup label="Transposed Json:" labelClassName={labelClass} contentClassName={contentClass} info={guidance.transposedData}>
                        <input
                            style={{ marginTop: '12px' }}
                            type="checkbox"
                            disabled={this.props.isReadOnly}
                            checked={this.props.config.isTransposed || false}
                            onChange={this.onTransposedDataChange} />
                    </FormGroup>
                }
                {this.state.showReportConfigsPopup &&
                    <Modal state={{ isOpen: true, title: 'Report configs', error: '' }} modalContentClasses='ingestionDetailsPopup' close={() => this.setState({ showReportConfigsPopup: false })}>
                        {this.props.config.reportConfigs.map((config) => {
                            return <div key={config.id} className='padding-bottom-5px'><a href={`${urlHelper.buildUrl(appRoutes.reporting.edit, { id: config.id })}`} target="_blank" rel="noopener noreferrer" className="className='padding-bottom-5px underline color-blue main-blue-color'">{config.name}</a></div>
                        })
                        }
                    </Modal>
                }
            </>
        );
    }
}

export default IngestionDetails;
